/**
 * Shared draw-gesture primitives for the leather / construction draw tools
 * (ShapeTool, CutoutTool, StitchTool, ConstructionTool). The first pieces
 * hoisted here under #804 are the two that were copy-pasted 4× — the
 * close-snap radius and the value-ray emit. The shared `DrawTool` base and the
 * polygon / drag-out gesture controllers land in later #804 stages; see
 * docs/draw-tool-architecture.md.
 */
import type { GuideRay, Scene } from "../canvas/Scene";
import {
  currentPointerKind,
  isCoarsePointer,
} from "../canvas/pointerPrecision";

/** Pull radius (project mm, at 1× — the tools compare raw distances) within
 *  which a draw gesture's hover snaps onto its first vertex to close the loop,
 *  and the size of the close-target halo. Was declared identically in all four
 *  draw tools before #804. */
export const CLOSE_SNAP_RADIUS = 6;

/** The least a finger's close-snap reaches, in screen px (#1150). The 6 mm
 *  above is already wider than a fingertip at working zoom, but it shrinks on
 *  screen as the view zooms out; this floor keeps it finger-sized there. */
const CLOSE_SNAP_TOUCH_PX = 24;

/** The close-snap radius in project mm for the pointer being handled: the
 *  fixed `CLOSE_SNAP_RADIUS`, never less than `CLOSE_SNAP_TOUCH_PX` on screen
 *  for a finger. Mouse and pen are unchanged. The halo stays drawn at
 *  `CLOSE_SNAP_RADIUS` — only the grab widens. */
export function closeSnapRadius(zoom: number): number {
  if (!isCoarsePointer(currentPointerKind())) return CLOSE_SNAP_RADIUS;
  return Math.max(
    CLOSE_SNAP_RADIUS,
    CLOSE_SNAP_TOUCH_PX / Math.max(zoom, 1e-4),
  );
}

/**
 * Build and publish a single on-canvas value ray (#744 / #788) running
 * `from` → `to`, optionally with an interior-angle marker back to `cornerPrev`.
 * Degenerate (zero-length) segments clear the rays instead. Rendering, the
 * `GuideRay` type, and the show/hide-rays toggle all live in `Scene`; this just
 * assembles the ray. Extracted verbatim from the four draw tools' private
 * `showSegmentRay` copies (#804 S1).
 */
export function emitSegmentRay(
  scene: Scene,
  from: { x: number; y: number },
  to: { x: number; y: number },
  cornerPrev?: { x: number; y: number },
  angleStepDeg?: number | null,
  angleRef?: "horizontal" | "corner" | null,
  /** Draw the `cornerPrev` arm as a dashed ghost rather than a solid ray. Set
   *  when the arm is a REFERENCE the angle was measured from (#162 — an
   *  existing line under the segment's start) rather than a segment of the
   *  shape being drawn. */
  cornerPrevGhost?: boolean,
): void {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) {
    scene.setGuideRays(null);
    return;
  }
  let deg = (Math.atan2(-dy, dx) * 180) / Math.PI;
  if (deg < 0) deg += 360;
  const ray: GuideRay = {
    from: { x: from.x, y: from.y },
    to: { x: to.x, y: to.y },
    lengthMm: length,
    angleDeg: deg,
    ...(cornerPrev ? { cornerPrev: { x: cornerPrev.x, y: cornerPrev.y } } : {}),
    ...(angleStepDeg != null && angleStepDeg > 0 ? { angleStepDeg } : {}),
    ...(angleRef != null ? { angleRef } : {}),
    ...(cornerPrevGhost ? { cornerPrevGhost: true } : {}),
  };
  scene.setGuideRays([ray]);
}
